import { Link } from "react-router-dom";
import NavBar from "@/components/NavBar";

const HERO = "https://images.unsplash.com/photo-1761095870661-c4ae15cac605?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwyfHxza2luY2FyZSUyMGJvdHRsZXMlMjBuZXV0cmFsJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3Nzg2NTAxMzF8MA&ixlib=rb-4.1.0&q=85";
// Hands only — applying serum from a dropper, soft natural light editorial
const PORTRAIT = "https://customer-assets.emergentagent.com/job_retinoid-companion/artifacts/are4u42t_serum%20dropper.jpg";

const VERDICTS = [
  { key: "green", label: "Green", desc: "Compatible with the protocol. A reasonable substitute.", swatch: "verdict-green" },
  { key: "yellow", label: "Yellow", desc: "Not harmful, but doesn't fit this phase. Pause, don't discard.", swatch: "verdict-yellow" },
  { key: "red", label: "Red", desc: "Works against your current phase. Skip.", swatch: "verdict-red" },
];

const PHASES = [
  { num: "01", title: "Skin Resilience", body: "Calm inflammation, restore the barrier, and rebuild tolerance. A simple AM/PM routine for your skin type — no actives yet.", tag: "Start here" },
  { num: "02", title: "Making Actives Count", body: "Once you've built a hydrated, resilient skin barrier, introduce actives that truly transform your skin — the right actives for *your* skin, in *this* stage of life.", tag: "Phase up when ready" },
];

// Lightweight inline-emphasis renderer: turns *word* into <em>word</em>.
function renderEmphasis(text) {
  const parts = String(text).split(/(\*[^*]+\*)/g);
  return parts.map((p, i) => p.startsWith("*") && p.endsWith("*")
    ? <em key={i} className="serif-italic">{p.slice(1, -1)}</em>
    : p);
}

export default function Landing() {
  return (
    <div className="App" data-testid="page-landing">
      <NavBar />

      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20">
        <div className="grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7 fade-in">
            <div className="aa-eyebrow mb-6">A guided skincare companion</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display leading-[1.05]">
              Choose products that help your skin <span className="serif-italic">thrive</span> — in perimenopause and beyond.
            </h1>
            <p className="mt-7 text-[var(--aa-text-2)] text-base sm:text-lg max-w-xl leading-relaxed">
              Built on Dr. Rey Hamidi's Ageless Alchemy protocols. Snap a label or paste an ingredient list — we'll tell you if it's compatible, worth pausing, or a no-go for this phase of life.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4">
              <Link
                to="/register"
                data-testid="cta-get-started"
                className="group inline-flex items-center justify-center gap-2 px-10 py-5 text-lg font-semibold rounded-full bg-[var(--aa-text)] text-[var(--aa-bg)] shadow-xl shadow-black/15 hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-300"
              >
                Begin your protocol
                <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
              </Link>
              <Link
                to="/login"
                data-testid="cta-login"
                className="inline-flex items-center justify-center px-10 py-5 text-lg font-medium rounded-full border-2 border-[var(--aa-text)] text-[var(--aa-text)] hover:bg-[var(--aa-text)] hover:text-[var(--aa-bg)] transition-colors duration-300"
              >
                I already have an account
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 fade-in delay-2">
            <div className="relative">
              <div className="aa-card overflow-hidden">
                <img src={HERO} alt="" className="w-full h-[420px] object-cover" />
              </div>
              <div className="absolute -bottom-8 -left-8 aa-card p-5 max-w-[260px] hidden md:block">
                <div className="aa-eyebrow mb-2">Phase 01</div>
                <div className="font-display text-lg leading-snug">Skin Resilience — quiet, strengthen, restore.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[var(--aa-bg-2)] py-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="aa-eyebrow mb-4">The Framework</div>
              <h2 className="font-display text-3xl lg:text-4xl leading-tight">Three answers. No noise.</h2>
              <p className="mt-5 text-[var(--aa-text-2)]">
                Every product you analyze comes back with one of three honest verdicts — the same green / yellow / red system used in the Ageless Alchemy protocols.
              </p>
            </div>
            <div className="lg:col-span-8 grid sm:grid-cols-3 gap-5">
              {VERDICTS.map((v, i) => (
                <div key={v.key} className={`aa-card p-6 fade-in delay-${i + 1}`} data-testid={`verdict-card-${v.key}`}>
                  <span className={`aa-pill ${v.swatch}`}>{v.label}</span>
                  <p className="mt-4 text-[var(--aa-text-2)] leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-20">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="aa-card overflow-hidden">
              <img src={PORTRAIT} alt="" className="w-full h-auto block" />
            </div>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <div className="aa-eyebrow mb-4">Two phases. One system.</div>
            <h2 className="font-display text-3xl lg:text-4xl leading-tight">First strengthen. <span className="serif-italic">Then transform.</span></h2>

            <div className="mt-10 space-y-5">
              {PHASES.map((p) => (
                <div key={p.num} className="aa-card p-6" data-testid={`phase-card-${p.num}`}>
                  <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
                    <div className="font-display text-xl">{p.num} — {p.title}</div>
                    <div className="aa-eyebrow">{p.tag}</div>
                  </div>
                  <p className="text-[var(--aa-text-2)]">{renderEmphasis(p.body)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[var(--aa-elevated)] py-10">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 flex flex-wrap items-center justify-between gap-4 text-sm text-[var(--aa-text-3)]">
          <div>© Ageless Alchemy Protocol Companion — a Dr. Rey Hamidi protocol companion.</div>
          <div className="aa-eyebrow">For educational use only.</div>
        </div>
      </footer>
    </div>
  );
}
